/**
 * SessionForm — formulario para crear una sesión de relevamiento
 *
 * @module survey/components
 */

import { useState } from 'react';
import { getSurveyApiError, surveyApi } from '../services/survey-api.service';
import type { SurveySession, SurveyCaptureMode } from '../types/survey.types';

interface Props {
  projectId: string;
  onCreated: (session: SurveySession) => void;
  onCancel: () => void;
}

const CAPTURE_MODES: { value: SurveyCaptureMode; label: string }[] = [
  { value: 'vehicle', label: 'Vehículo' },
  { value: 'bicycle', label: 'Bicicleta' },
  { value: 'pedestrian', label: 'Peatonal' },
  { value: 'drone', label: 'Drone' },
  { value: 'static', label: 'Estático' },
];

export function SessionForm({ projectId, onCreated, onCancel }: Props): JSX.Element {
  const [name, setName] = useState('');
  const [captureDate, setCaptureDate] = useState('');
  const [captureMode, setCaptureMode] = useState<SurveyCaptureMode>('vehicle');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      const session = await surveyApi.createSession({
        projectId,
        name: name.trim(),
        captureMode,
        captureDate: captureDate || undefined,
      });
      onCreated(session);
    } catch (err) {
      setError(getSurveyApiError(err));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form className="sw-form" onSubmit={e => void handleSubmit(e)}>
      <h3 className="sw-form-title">Nueva sesión</h3>

      <label className="sw-form-field">
        <span className="sw-form-label">Nombre *</span>
        <input
          className="sw-form-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ej: Recorrido calle Principal"
          maxLength={200}
          required
        />
      </label>

      <label className="sw-form-field">
        <span className="sw-form-label">Fecha de captura</span>
        <input
          className="sw-form-input"
          type="date"
          value={captureDate}
          onChange={e => setCaptureDate(e.target.value)}
        />
      </label>

      <label className="sw-form-field">
        <span className="sw-form-label">Modo de captura *</span>
        <select
          className="sw-form-input"
          value={captureMode}
          onChange={e => setCaptureMode(e.target.value as SurveyCaptureMode)}
        >
          {CAPTURE_MODES.map(m => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      {error && <p className="sw-form-error">{error}</p>}

      <div className="sw-form-actions">
        <button type="submit" className="sw-btn sw-btn-primary" disabled={isSaving}>
          {isSaving ? 'Creando...' : 'Crear sesión'}
        </button>
        <button type="button" className="sw-btn sw-btn-ghost" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
